/**
 * DocViewerPage — standalone full-page PDF viewer opened in a NEW TAB from Process
 * Docs/Receipts ("open in new tab"). It renders the document to <canvas> via pdf.js
 * (PdfCanvasViewer), so it ALWAYS shows inline — unaffected by Chrome's "Download
 * PDFs instead of opening them" setting (which would force a Save-As if the browser
 * were handed a real .pdf).
 *
 * Route: /doc-view?doc=<document_id>  (standalone, no dashboard chrome).
 * Uses the caller's existing localStorage session to fetch the doc bytes.
 */
import React, { useEffect, useState } from 'react';
import PdfCanvasViewer, { VIEW_MODES, ViewMode } from '../../../components/ui/PdfCanvasViewer';
import { checkApi, getTableData } from '../../db-viewer/data/dataAccess';
import { fetchTableRow } from '../../db-viewer/data/dbViewerService';
import { decompressBase64ToBytes } from '../utils/pdfBytes';
import { resolveDocBase64 } from '../utils/docStorage';

const wrap: React.CSSProperties = { position: 'fixed', inset: 0, display: 'flex', flexDirection: 'column', background: '#525659' };
const bar: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 10, padding: '6px 12px', background: '#323639', color: '#d4d4d4', font: '13px system-ui, sans-serif', flexShrink: 0 };
const msg: React.CSSProperties = { color: '#d4d4d4', font: '14px system-ui', textAlign: 'center', margin: 'auto', padding: 28 };

const DocViewerPage: React.FC = () => {
  const [bytes, setBytes] = useState<Uint8Array | null>(null);
  const [name, setName] = useState('Document');
  const [error, setError] = useState('');
  const [viewMode, setViewMode] = useState<ViewMode>('fit');
  const [zoom, setZoom] = useState(1);

  useEffect(() => {
    const id = Number(new URLSearchParams(window.location.search).get('doc'));
    if (!id) { setError('No document specified.'); return; }
    let cancelled = false;
    (async () => {
      // Fetch ONLY this doc's row (single-row API; never the whole table — that
      // returns every doc's bytes). Fall back to the local metadata cache.
      // Wait for the API check: this page is often freshly loaded (an iframe), and
      // isApiReady() alone is still false then — the local cache holds no documents,
      // so the viewer said "Document not found." (Masters › View receipt, 2026-10-19).
      let row: any = null;
      if (await checkApi().catch(() => false)) {
        try { row = await fetchTableRow('dms_documents', id); } catch { /* fall through */ }
      }
      if (!row) {
        const rows = getTableData('dms_documents');
        // eslint-disable-next-line eqeqeq
        row = rows.find((d: any) => d.document_id == id) || null;
      }
      if (!row) { if (!cancelled) setError('Document not found.'); return; }
      const filename = row.filename || row.original_filename || 'Document';
      if (!cancelled) { setName(filename); document.title = filename; }
      // Bytes come from the blob store (file_path) with a fallback to inline base64.
      const base64 = await resolveDocBase64(row);
      if (!base64) { if (!cancelled) setError('This document has no stored file.'); return; }
      try {
        const b = await decompressBase64ToBytes(base64);
        if (!cancelled) setBytes(b);
      } catch {
        if (!cancelled) setError('Could not open this document.');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div style={wrap}>
      <div style={bar}>
        <span style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{name}</span>
        {bytes && (
          <>
            <button onClick={() => setZoom(z => Math.max(0.25, +(z - 0.15).toFixed(2)))} style={btn}>−</button>
            <select value={viewMode} onChange={e => setViewMode(e.target.value as ViewMode)} style={sel}>
              {VIEW_MODES.map(m => <option key={m.key} value={m.key}>{m.label}</option>)}
            </select>
            <button onClick={() => setZoom(z => Math.min(4, +(z + 0.15).toFixed(2)))} style={btn}>+</button>
          </>
        )}
      </div>
      {error ? (
        <div style={msg}>{error}</div>
      ) : !bytes ? (
        <div style={msg}>Loading…</div>
      ) : (
        <div style={{ flex: 1, minHeight: 0 }}>
          <PdfCanvasViewer pdfBytes={bytes} title={name} viewMode={viewMode} zoom={zoom} />
        </div>
      )}
    </div>
  );
};

const btn: React.CSSProperties = { width: 26, height: 26, border: '1px solid #555', borderRadius: 4, background: '#404346', color: '#d4d4d4', cursor: 'pointer', fontSize: 15 };
const sel: React.CSSProperties = { border: '1px solid #555', borderRadius: 4, background: '#404346', color: '#d4d4d4', fontSize: 12, padding: '3px 6px' };

export default DocViewerPage;
